import { useMemo, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Copy, Download, ExternalLink, Link2, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import FaqAccordion from "@/components/FaqAccordion";
import FinalCta from "@/components/FinalCta";
import { SectionHeading, PlatformGrid } from "@/components/Sections";
import { useSeo, FaqSchema } from "@/lib/seo";

const FAQS = [
  { q: "What is a WhatsApp link?", a: "A wa.me link opens a WhatsApp chat with your number when someone taps it — on phone or desktop — optionally with a message already typed in. No need for customers to save your number first." },
  { q: "Is this generator free?", a: "Yes. Create as many links and QR codes as you like. Nothing is stored — the link is built in your browser." },
  { q: "Which number format should I use?", a: "Enter your full number with country code, e.g. 91 98765 43210. A 10-digit number is treated as an Indian (+91) number." },
  { q: "Where can I use the link?", a: "On your website, Instagram bio, Google Business Profile, email signature, ads, invoices and printed material (use the QR code)." },
  { q: "Can I get a printable poster instead?", a: "Yes — GrabNChat's QR Kits turn your WhatsApp link into a branded, print-ready poster." },
];

function toDigits(raw) {
  let d = String(raw || "").replace(/\D/g, "");
  if (d.length === 10) d = `91${d}`;
  return /^\d{7,15}$/.test(d) ? d : null;
}

export default function LinkGeneratorPage() {
  useSeo({
    title: "Free WhatsApp Link Generator with QR Code | GrabNChat",
    description: "Create a free wa.me WhatsApp chat link with a pre-filled message and download its QR code in seconds.",
  });
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("Hi! I'd like to know more.");
  const canvasRef = useRef(null);
  const digits = toDigits(phone);
  const invalid = phone.trim() !== "" && !digits;
  const link = useMemo(() => {
    if (!digits) return "";
    const m = message.trim();
    return `https://wa.me/${digits}${m ? `?text=${encodeURIComponent(m)}` : ""}`;
  }, [digits, message]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy — please copy it manually."); }
  };
  const download = () => {
    const c = canvasRef.current; if (!c) return;
    const a = document.createElement("a");
    a.href = c.toDataURL("image/png"); a.download = `whatsapp-qr-${digits}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div data-testid="link-generator-page">
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white">
        <div className="absolute inset-0 bg-dots opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-16">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-widest text-[#25D366] font-semibold mb-4">Free tool</div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">Free WhatsApp Link Generator</h1>
            <p className="mt-5 text-lg text-slate-600">Create a click-to-chat link and QR code for your WhatsApp number in seconds — add it to your website, Instagram bio, ads and print.</p>
          </div>
          <div className="mt-10 grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7 min-w-0 bg-white border border-slate-200 rounded-3xl p-6 md:p-8 space-y-5 shadow-sm">
              <div>
                <Label htmlFor="lg-phone" className="font-semibold text-slate-900">WhatsApp number (with country code)</Label>
                <Input id="lg-phone" className="mt-2" inputMode="tel" placeholder="e.g. 91 98765 43210" value={phone} onChange={(e) => setPhone(e.target.value)} aria-invalid={invalid} data-testid="lg-phone" />
                {invalid && <p className="mt-1.5 text-xs text-red-600">Enter a valid number with country code.</p>}
              </div>
              <div>
                <Label htmlFor="lg-msg" className="font-semibold text-slate-900">Pre-filled message (optional)</Label>
                <Textarea id="lg-msg" className="mt-2" rows={3} maxLength={300} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="lg-message" />
              </div>
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 flex items-center gap-3">
                <Link2 className="w-4 h-4 text-slate-500 shrink-0" />
                <code className="flex-1 min-w-0 truncate text-sm text-slate-700" data-testid="lg-link">{link || "Your link will appear here"}</code>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button type="button" disabled={!link} onClick={copy} className="bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full h-11 font-semibold" data-testid="lg-copy"><Copy className="w-4 h-4 mr-2" /> Copy link</Button>
                <Button type="button" disabled={!link} onClick={download} variant="outline" className="rounded-full h-11 font-semibold" data-testid="lg-download"><Download className="w-4 h-4 mr-2" /> Download QR</Button>
                {link && (
                  <a href={link} target="_blank" rel="noopener noreferrer">
                    <Button type="button" variant="outline" className="w-full rounded-full h-11 font-semibold"><ExternalLink className="w-4 h-4 mr-2" /> Test link</Button>
                  </a>
                )}
              </div>
            </div>
            <div className="lg:col-span-5 min-w-0 flex flex-col items-center bg-white border border-slate-200 rounded-3xl p-8">
              {link ? (
                <QRCodeCanvas ref={canvasRef} value={link} size={512} level="M" marginSize={2} style={{ width: 240, height: 240 }} />
              ) : (
                <div className="w-60 h-60 rounded-2xl bg-slate-50 border border-dashed border-slate-300 grid place-items-center text-slate-400"><QrCode className="w-16 h-16" /></div>
              )}
              <p className="mt-4 text-sm text-slate-500 text-center">Scan to test. Want a branded, printable poster?</p>
              <Link to="/products/qr-kits#try-it" className="mt-1 text-sm font-semibold text-[#25D366]">Try the QR Poster Builder →</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading center title="WhatsApp link FAQs" />
          <div className="mt-10"><FaqAccordion faqs={FAQS} /></div>
          <FaqSchema faqs={FAQS} />
        </div>
      </section>
      <PlatformGrid limit={6} title="Do more than a link with GrabNChat" sub="Automate replies, run campaigns and manage every chat from one inbox." />
      <FinalCta />
    </div>
  );
}
